import React, { useEffect, useState, useRef, useCallback } from "react";
import { connect } from "react-redux";
import { fetchaccommodaties } from "../../Reducers/actions";
import { Link } from "react-router-dom";
import Chart from "react-apexcharts";
import moment from "moment";

function FavSesnorenWidget({ accommodaties, fetchaccommodaties }) {
  const [load, setLoad] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const observer = useRef();
  
  useEffect(() => {
    setLoad(true);
    setCurrentPage(1);
    setHasMore(true);
    fetchaccommodaties(6, search, 1).then((result) => {
      setLoad(false);
      // Check if we got less than expected, meaning no more pages
      if (!result || (Array.isArray(result.results) && result.results.length < 6)) {
        setHasMore(false);
      }
    });

    // Auto-refresh every 30 seconds
    const interval = setInterval(() => {
      setCurrentPage(1);
      setHasMore(true);
      fetchaccommodaties(6, search, 1);
    }, 30000);

    return () => clearInterval(interval);
  }, [search]);

  const lastAccommodatieRef = useCallback(node => {
    if (load || loadingMore) return;
    if (observer.current) observer.current.disconnect();
    observer.current = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting && hasMore) {
        loadMoreAccommodaties();
      }
    });
    if (node) observer.current.observe(node);
  }, [load, loadingMore, hasMore]);

  const loadMoreAccommodaties = async () => {
    if (loadingMore || !hasMore) return;
    setLoadingMore(true);
    
    const nextPage = currentPage + 1;
    try {
      const result = await fetchaccommodaties(6, search, nextPage);
      setCurrentPage(nextPage);
      
      // Check if we got less than expected, meaning no more pages
      if (!result || (Array.isArray(result.results) && result.results.length < 6)) {
        setHasMore(false);
      }
    } catch (error) {
      console.error('Error loading more accommodaties:', error);
    }
    setLoadingMore(false);
  };
  function isalert(sensor) {
    if (sensor.active === false) return "switchoff";
    if (sensor.status === "false") return "alerttemp";
    else return "active";
  }

  function graph(row) {
    // Prepare data for ApexCharts sparkline
    const data = [];
    if (row.meter_data && row.meter_data.length > 0) {
      row.meter_data.map(function (a, key) {
        if (key > 0) {
          const consumption = Number(row.meter_data[key - 1].value - Number(a.value));
          data.push(consumption);
        }
        return a;
      });
    }

    // ApexCharts sparkline configuration
    const options = {
      chart: {
        type: "line",
        height: 60,
        sparkline: {
          enabled: true,
        },
        animations: {
          enabled: false,
        },
        zoom: {
          enabled: false,
        },
      },
      stroke: {
        curve: "smooth",
        width: 2,
      },
      colors: ["#848c45"],
      tooltip: {
        enabled: true,
        theme: "dark",
        x: {
          show: false,
        },
        y: {
          formatter: function (val) {
            return val.toFixed(2) + " kWh";
          },
        },
      },
      grid: {
        show: false,
      },
      xaxis: {
        labels: {
          show: false,
        },
        axisBorder: {
          show: false,
        },
        axisTicks: {
          show: false,
        },
      },
      yaxis: {
        labels: {
          show: false,
        },
      },
    };

    const series = [
      {
        name: "Verbruik",
        data: data.reverse().slice(-20), // Last 20 data points
      },
    ];

    return (
      <div style={{ width: "100%", height: "60px" }}>
        {data.length > 0 ? (
          <Chart options={options} series={series} type="line" height={60} />
        ) : (
          <div
            style={{
              height: "60px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#999",
              fontSize: "0.8rem",
            }}
          >
            Geen data beschikbaar
          </div>
        )}
      </div>
    );
  }
  function lastvalue(row) {
    if (row.meter_data && row.meter_data.length > 0) {
      return Number(row.meter_data[0].value).toFixed(2);
    } else {
      return null;
    }
  }

  function isOffline(sensor) {
    // Check if any meter has recent data
    if (sensor.meters && sensor.meters.length > 0) {
      return sensor.meters.some(meter => {
        if (meter.meter_data && meter.meter_data[0]) {
          const lastDatum = moment(meter.meter_data[0].datum);
          return moment().diff(lastDatum, "hours") >= 1;
        }
        return true;
      });
    }
    return true; // geen data -> beschouwen als offline
  }
  const handleSearchChange = (e) => {
    if (e.target.value.length > 2 || e.target.value.length === 0) {
      setLoad(true);
      setCurrentPage(1);
      setHasMore(true);
      setSearch(e.target.value);
    } else {
      setSearch(e.target.value);
    }
  };

  const getMeterIcon = (meter, size) => {
    switch (meter) {
      case "water":
        return <i style={{ fontSize: size + "rem", color: "#3baa35" }} className="fa-solid fa-droplet"></i>;
      case "gas":
        return <i style={{ fontSize: size + "rem", color: "#3baa35" }} className="fa-solid fa-fire"></i>;
      case "electra":
        return <i style={{ fontSize: size + "rem", color: "#3baa35" }} className="fa-solid fa-bolt"></i>;
      default:
        return null;
    }
  };

  function getKwh(meter, kWh) {
    switch (meter) {
      case "water":
        return (kWh/1000).toFixed(2) + " Kub";
      case "gas":
        return kWh + " Kub";
      case "electra":
        return kWh + " kWh";
      default:
        return null;
    }
  }

  return (
    <div className="row gx-0">
      <div className="col-12 p-3">
        <div className="row">
          <div className="col-12 mb-3">
            <label htmlFor="search-bar-0" className="search-label">
              <span id="search-bar-0-label" className="sr-only">
                Zoek de accommodatie
              </span>
              <input 
                id="search-bar-0" 
                onChange={handleSearchChange} 
                type="text" 
                aria-labelledby="search-bar-0-label" 
                className="form-control" 
                placeholder="Zoeken naar accommodatie..." 
                value={search} 
              />
            </label>
          </div>
        </div>
      </div>

      {load ? (
        <div className="col-lg-12">
          <div className="alert alert-info">Laden...</div>
        </div>
      ) : accommodaties.length > 0 ? (
        <>
          {accommodaties.map((sensor, key) => (
            <div 
              key={key} 
              className="col-lg-4 col-md-6 col-sm-12 d-flex p-3"
              ref={key === accommodaties.length - 1 ? lastAccommodatieRef : null}
            >
              <div className={`campsite-card accommodation-card ${!sensor.active ? "disabled" : ""} ${isOffline(sensor) ? "offline" : ""}`}>
              
              {/* Header with accommodation name */}
              <div className="campsite-header">
                <div className="location-number">
                  <i className="fa-solid fa-house"></i> {sensor.naam}
                </div>
                <div className="power-status">
                  <div className={`power-indicator ${sensor.active ? "on" : "off"}`}>
                    <div className="power-icon"></div>
                    <span>{sensor.active ? "Actief" : "Uitgeschakeld"}</span>
                  </div>
                </div>
              </div>

              {/* Meters overview */}
              <div className="meters-overview">
                {sensor.meters && sensor.meters.length > 0 ? (
                  sensor.meters.map((meter, meterKey) => (
                    <div key={meterKey} className="meter-item">
                      <div className="meter-header">
                        <div className="meter-icon">
                          {getMeterIcon(meter.soort, 1.5)}
                        </div>
                        <div className="meter-info">
                          <span className="meter-type">{meter.soort}</span>
                          <span className="meter-value">{getKwh(meter.soort, lastvalue(meter))}</span>
                        </div>
                      </div>
                      
                      {/* Sparkline chart for this meter */}
                      <div className="chart-container">
                        {graph(meter)}
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="no-meters">
                    <i className="fas fa-info-circle"></i>
                    <span>Geen meters beschikbaar</span>
                  </div>
                )}
              </div>

              {/* Status indicators */}
              {isOffline(sensor) && (
                <div className="offline-warning">
                  <i className="fas fa-exclamation-triangle"></i>
                  Offline - Geen recente data
                </div>
              )}

              {/* Footer with link */}
              <div className="card-footer-new">
                <Link className="btn btn-outline-primary btn-sm w-100" to={`/standplaatsen/accommodaties/show/${sensor._id}`}>
                  <i className="fas fa-eye"></i> Bekijk accommodatie
                </Link>
              </div>
            </div>
          </div>
          ))}
          
          {/* Loading indicator for infinite scroll */}
          {loadingMore && (
            <div className="col-lg-12">
              <div className="alert alert-info text-center">
                <i className="fas fa-spinner fa-spin"></i> Meer accommodaties laden...
              </div>
            </div>
          )}
          
          {/* No more results indicator */}
          {!hasMore && accommodaties.length > 0 && (
            <div className="col-lg-12">
              <div className="alert alert-secondary text-center">
                <i className="fas fa-info-circle"></i> Alle accommodaties zijn geladen
              </div>
            </div>
          )}
        </>
      ) : (
        <div className="col-lg-12">
          <div className="alert alert-info">
            <i className="fas fa-info-circle"></i> Er zijn geen accommodaties gevonden.
          </div>
        </div>
      )}
    </div>
  );
}

function mapStateToProps(state) {
  return { user: state.auth.user, accommodaties: state.standplaatsen.accommodaties };
}
export default connect(mapStateToProps, { fetchaccommodaties })(FavSesnorenWidget);
